import React, { useState, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import ExpandMore from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import CardQuickViewModal from '../CardQuickViewModal';
import CardCoverImage from '../CardCoverImage';
import { STYLE_OPTIONS } from '../../lib/styleTaxonomy';
import { samplesForStyle } from '../../lib/styleSamples';

const TEAL = '#206884';

// Home-card size tokens — a sample thumbnail is IDENTICAL in footprint to a home browse tile.
const THUMB_WIDTH = { xs: '28vw', sm: 150, md: 160 };
const THUMB_MAX_WIDTH = { xs: 150, sm: 160, md: 180 };

// A bare, style-reset <button> — no MUI ButtonBase (its ripple sets up timers that slow the wizard
// tests and leak act() warnings). Plain buttons keep the accordions cheap to render + fully accessible.
const resetBtn = {
  m: 0,
  border: 0,
  background: 'transparent',
  font: 'inherit',
  color: 'inherit',
  cursor: 'pointer',
  textAlign: 'left',
};

// One style accordion. Memoized so selecting/expanding a style re-renders only the two affected rows
// (old + new), not all 18 — keeps the step responsive and cheap in tests.
const StyleAccordion = React.memo(function StyleAccordion({ style, selected, expanded, onToggle, onSelect, onPreview }) {
  const StyleIcon = style.icon;
  const samples = samplesForStyle(style.name);
  return (
    <Box
      sx={{
        border: `1px solid ${selected ? TEAL : '#e7e3d6'}`,
        borderRadius: '12px',
        overflow: 'hidden',
        backgroundColor: '#fff',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: '12px 14px' }}>
        {/* Toggle: icon + name + blurb. Clicking shows/hides this style's examples. */}
        <Box
          component="button"
          type="button"
          onClick={() => onToggle(style.name)}
          aria-expanded={expanded}
          aria-label={`${style.name} — ${style.blurb}`}
          sx={{ ...resetBtn, flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1.25 }}
        >
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: '9px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: `${style.color}1f`,
              flexShrink: 0,
            }}
          >
            <StyleIcon sx={{ color: style.color, fontSize: 20 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600, lineHeight: 1.2, color: selected ? TEAL : 'inherit' }}>
              {style.name}
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{style.blurb}</Typography>
          </Box>
        </Box>

        {/* Select pill — single-select. A sibling button, so it selects WITHOUT toggling. */}
        <Box
          component="button"
          type="button"
          onClick={() => onSelect(style.name)}
          aria-pressed={selected}
          aria-label={selected ? `${style.name} selected` : `Select ${style.name}`}
          sx={{
            ...resetBtn,
            flexShrink: 0,
            borderRadius: '8px',
            px: 1.5,
            py: 0.5,
            fontSize: '0.8rem',
            fontWeight: 700,
            border: `1px solid ${TEAL}`,
            color: selected ? '#fff' : TEAL,
            backgroundColor: selected ? TEAL : 'transparent',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            whiteSpace: 'nowrap',
          }}
        >
          {selected && <CheckIcon sx={{ fontSize: 16 }} />}
          {selected ? 'Selected' : 'Select'}
        </Box>

        {/* Chevron — also toggles. */}
        <Box
          component="button"
          type="button"
          onClick={() => onToggle(style.name)}
          aria-label={`${expanded ? 'Collapse' : 'Expand'} ${style.name}`}
          sx={{ ...resetBtn, flexShrink: 0, display: 'flex', alignItems: 'center', p: 0.25 }}
        >
          <ExpandMore sx={{ color: '#b3bbb6', transform: expanded ? 'rotate(180deg)' : 'none' }} />
        </Box>
      </Box>

      {expanded && (
        <Box sx={{ p: '2px 14px 14px' }}>
          <Box
            data-testid={`style-thumbs-${style.name}`}
            sx={{
              display: 'flex',
              gap: 1.5,
              overflowX: 'auto',
              pb: 0.75,
              scrollSnapType: 'x mandatory',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {samples.length > 0 ? (
              samples.map((url, i) => (
                <Box
                  key={url + i}
                  component="button"
                  type="button"
                  onClick={() => onPreview(style.name, i)}
                  aria-label={`Preview ${style.name} example ${i + 1}`}
                  data-testid={`style-thumb-${style.name}`}
                  sx={{
                    ...resetBtn,
                    p: 0,
                    flex: '0 0 auto',
                    width: THUMB_WIDTH,
                    maxWidth: THUMB_MAX_WIDTH,
                    scrollSnapAlign: 'start',
                  }}
                >
                  <CardCoverImage
                    coverImageUrl={url}
                    alt={`${style.name} example ${i + 1}`}
                    sx={{
                      width: '100%',
                      aspectRatio: '5 / 7',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    }}
                  />
                </Box>
              ))
            ) : (
              <Box
                component="button"
                type="button"
                onClick={() => onPreview(style.name, 0)}
                aria-label={`Preview ${style.name}`}
                data-testid={`style-placeholder-${style.name}`}
                sx={{
                  ...resetBtn,
                  flex: '0 0 auto',
                  width: THUMB_WIDTH,
                  maxWidth: THUMB_MAX_WIDTH,
                  aspectRatio: '5 / 7',
                  borderRadius: '8px',
                  border: '1px dashed #d8d2c2',
                  backgroundColor: '#f6f3ea',
                  color: '#b3bbb6',
                  fontSize: '0.72rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  p: 0.5,
                  scrollSnapAlign: 'start',
                }}
              >
                Sample soon
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
});

/**
 * "Pick a style" step — the approved accordion wizard (docs/mockups/store-style-step.html). ONE
 * accordion PER style (18 total; styles have no families). Each header: a tinted icon chip + name +
 * blurb + a Select pill + a chevron. Tapping the header/chevron TOGGLES that style's examples; the
 * Select pill SELECTS the style (single-select — selecting another replaces it) without toggling
 * (the pill is a sibling button, so it never fires the toggle). All collapsed on load. A selected
 * style is marked with a teal border + teal name + a filled "Selected" pill. The expanded body is a
 * horizontally-scrollable rail of HOME-SIZED example cards; tapping one opens the shared looping
 * lightbox with an honest "i / total" count + "Use this style".
 *
 * Props: `{ styleId, onSelect }` — `styleId` is the single selected style name; `onSelect(name)` sets it.
 */
const StyleStep = ({ styleId = '', onSelect }) => {
  const [open, setOpen] = useState(new Set()); // all collapsed on load
  const [preview, setPreview] = useState(null); // { name, index }

  const toggle = useCallback((name) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  const openPreview = useCallback((name, index) => setPreview({ name, index }), []);
  const handleSelect = useCallback((name) => onSelect && onSelect(name), [onSelect]);
  const closePreview = () => setPreview(null);
  const confirmPreview = () => {
    if (preview && typeof onSelect === 'function') onSelect(preview.name);
    closePreview();
  };

  const previewSamples = preview ? samplesForStyle(preview.name) : [];
  const previewItems = previewSamples.map((url, i) => ({
    coverImageUrl: url,
    alt: `${preview ? preview.name : ''} example ${i + 1}`,
  }));
  const previewIndex = preview ? preview.index : 0;

  return (
    <Box>
      <Typography component="h1" sx={{ fontSize: '1.4rem', fontWeight: 700, mb: 1 }}>
        Pick a style
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        This sets the medium — the look and feel of the art. Open a style to see a few examples, then
        pick what fits.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {STYLE_OPTIONS.map((style) => (
          <StyleAccordion
            key={style.name}
            style={style}
            selected={styleId === style.name}
            expanded={open.has(style.name)}
            onToggle={toggle}
            onSelect={handleSelect}
            onPreview={openPreview}
          />
        ))}
      </Box>

      <CardQuickViewModal
        open={!!preview}
        onClose={closePreview}
        items={previewItems.length ? previewItems : undefined}
        index={previewIndex}
        onIndexChange={(next) => setPreview((p) => (p ? { ...p, index: next } : p))}
        count={previewItems.length > 1 ? { index: previewIndex, total: previewItems.length } : undefined}
        alt={preview ? `${preview.name} example` : ''}
        actionLabel="Use this style"
        onConfirm={confirmPreview}
        ariaLabel={preview ? `${preview.name} example preview` : 'Style example preview'}
        testId="style-lightbox"
      />
    </Box>
  );
};

export default StyleStep;
